/**
 * Отбор игроков в адресной строке.
 *
 * Раньше фильтры жили только в памяти страницы: показать коллеге «тир 3 без
 * каптов» можно было лишь на словах, а обновление страницы сбрасывало отбор.
 * Теперь отбор и сортировка читаются из адреса и в него же пишутся, поэтому
 * ссылку можно скопировать и отправить.
 *
 * Разбор нарочно снисходителен: адрес правят руками и присылают в чат, где
 * его ломает перенос строки. Непонятное значение здесь — это отсутствующее
 * значение, а не пустая страница с ошибкой.
 */

import { TIERS, type Tier } from '@ak/shared';

import type { PlayerActivity, PlayerFilters, PlayerProblem, PlayerStatus } from './player-filter.ts';
import { isSortKey, type PlayerSortKey } from './player-sort.ts';

/** Что показывает страница игроков: отбор плюс порядок строк. */
export interface PlayerView {
  filters: PlayerFilters;
  sortKey: PlayerSortKey;
  ascending: boolean;
}

export const DEFAULT_SORT_KEY: PlayerSortKey = 'tier';

const STATUSES: readonly PlayerStatus[] = ['all', 'present', 'left'];
const ACTIVITIES: readonly PlayerActivity[] = ['all', 'week', 'month', 'stale'];
const PROBLEMS: readonly PlayerProblem[] = ['noStatic', 'noCaptures', 'unseen'];

function oneOf<T extends string>(allowed: readonly T[], value: string | null, fallback: T): T {
  return allowed.find((item) => item === value) ?? fallback;
}

/** Порог из адреса. Ноль и минус порогом быть не могут: они никого не отсекают. */
function threshold(value: string | null): number | null {
  if (value === null) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Значения из списка — в порядке самого списка.
 *
 * Отбирается пересечение с известными значениями, поэтому мусор из адреса
 * отсеивается сам, а порядок не зависит от того, как их написали в ссылке:
 * иначе две одинаковые по смыслу ссылки давали бы разные адреса.
 */
function pickMany<T extends string>(allowed: readonly T[], value: string | null): T[] {
  if (value === null) {
    return [];
  }

  const parts = value.split(',');
  return allowed.filter((item) => parts.includes(item));
}

function tiersFrom(value: string | null): Tier[] {
  if (value === null) {
    return [];
  }

  const parts = value.split(',');
  return TIERS.filter((tier) => parts.includes(String(tier)));
}

export function parsePlayerView(search: string): PlayerView {
  const params = new URLSearchParams(search);
  const sort = params.get('sort');

  return {
    filters: {
      query: params.get('q') ?? '',
      tiers: tiersFrom(params.get('tiers')),
      status: oneOf(STATUSES, params.get('status'), 'all'),
      activity: oneOf(ACTIVITIES, params.get('activity'), 'all'),
      problems: pickMany(PROBLEMS, params.get('problems')),
      minCaptures: threshold(params.get('minCaptures')),
      minAvgDamage: threshold(params.get('minAvgDamage')),
      minAvgKills: threshold(params.get('minAvgKills')),
    },
    sortKey: sort !== null && isSortKey(sort) ? sort : DEFAULT_SORT_KEY,
    // Порядок по умолчанию — по возрастанию, поэтому в адресе отмечается
    // только обратный.
    ascending: params.get('dir') !== 'desc',
  };
}

/**
 * Отбор из адреса открытой страницы.
 *
 * Чтение `window` живёт здесь, а не в разметке: экранные тесты рисуют
 * страницы сервером, браузера там нет вовсе, и обращение к адресу прямо в
 * компоненте роняло их с `window is not defined`.
 */
export function currentPlayerView(): PlayerView {
  return parsePlayerView(typeof window === 'undefined' ? '' : window.location.search);
}

/**
 * Адрес для текущего отбора.
 *
 * Значения по умолчанию не пишутся: без этого адрес обрастал бы хвостом
 * `status=all&activity=all&dir=asc` на пустом отборе, и отличить «ничего не
 * выбрано» от «выбрано и сброшено» было бы нельзя даже глазами.
 */
export function playerViewToQuery(view: PlayerView): string {
  const params = new URLSearchParams();
  const { filters } = view;

  const query = filters.query.trim();
  if (query !== '') {
    params.set('q', query);
  }
  if (filters.tiers.length > 0) {
    params.set('tiers', filters.tiers.join(','));
  }
  if (filters.status !== 'all') {
    params.set('status', filters.status);
  }
  if (filters.activity !== 'all') {
    params.set('activity', filters.activity);
  }
  if (filters.problems.length > 0) {
    params.set('problems', filters.problems.join(','));
  }
  if (filters.minCaptures !== null) {
    params.set('minCaptures', String(filters.minCaptures));
  }
  if (filters.minAvgDamage !== null) {
    params.set('minAvgDamage', String(filters.minAvgDamage));
  }
  if (filters.minAvgKills !== null) {
    params.set('minAvgKills', String(filters.minAvgKills));
  }
  if (view.sortKey !== DEFAULT_SORT_KEY) {
    params.set('sort', view.sortKey);
  }
  if (!view.ascending) {
    params.set('dir', 'desc');
  }

  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}
